import type { TFunction } from 'i18next';
import type { GridColDef } from '@mui/x-data-grid-premium';

import { showDeleteDialog } from 'app/main/components';
import { ViewTableUtils } from 'app/main/components/page-layouts';

import KpisUtils, { type KpisGoalsValuesAggregationType } from './components/KpisUtils';
import { showKpisGoalsEditDialog } from './components/KpisGoalsEditDialog';

type KpisGoalsValuesTableColumnsType = (params: {
	settings: any;
	aggregation?: KpisGoalsValuesAggregationType;
	t: TFunction;
	updateRefresh: VoidFunction;
}) => GridColDef[];

const getKpisGoalsValuesTableColumns: KpisGoalsValuesTableColumnsType = ({
	settings,
	aggregation,
	t,
	updateRefresh
}) => {
	const columns: GridColDef[] = [
		{
			field: 'kpis_title',
			headerName: t('DETAILS_KPIS_GOALS_FIELD_KPI'),
			minWidth: 200,
			flex: 1,
			renderCell: ViewTableUtils.renderCellWithTooltip,
			hideable: false
		}
	];

	if (!aggregation) {
		columns.push({
			field: 'kgvl_aggregation_module_title',
			headerName: t('DETAILS_KPIS_GOALS_FIELD_AGGREGATION_TITLE'),
			minWidth: 200,
			flex: 2,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderCellWithTooltip({
					className: !row.kpgl_aggregation_module_value ? 'text-red' : undefined,
					value,
					to: { module: row.kpis_aggregation_module, uid: row.kgvl_aggregation_module_value }
				}),
			hideable: false
		});
	}

	columns.push(
		{
			field: 'kpgl_d_from',
			headerName: t('DETAILS_KPIS_GOALS_FIELD_DATE_FROM'),
			minWidth: 120,
			renderCell: ViewTableUtils.renderDate,
			hideable: false
		},
		{
			field: 'kpgl_d_to',
			headerName: t('DETAILS_KPIS_GOALS_FIELD_DATE_TO'),
			minWidth: 120,
			renderCell: ViewTableUtils.renderDate,
			hideable: false
		},
		{
			field: 'kgvl_value',
			type: 'number',
			headerName: t('DETAILS_KPIS_GOALS_FIELD_CURRENT_VALUE'),
			minWidth: 150,
			renderCell: KpisUtils.renderKpiValue,
			hideable: false
		},
		{
			field: 'kpgl_value',
			type: 'number',
			headerName: t('DETAILS_KPIS_GOALS_FIELD_TARGET_VALUE'),
			minWidth: 150,
			renderCell: KpisUtils.renderKpiTarget,
			hideable: false
		},
		{
			field: 'kpgl_value_residual',
			type: 'number',
			headerName: t('DETAILS_KPIS_GOALS_FIELD_RESIDUAL_VALUE'),
			minWidth: 150,
			renderCell: KpisUtils.renderKpiValue
		},
		{
			field: 'kpgl_value_progress',
			type: 'number',
			headerName: t('DETAILS_KPIS_GOALS_FIELD_PROGRESS'),
			minWidth: 100,
			renderCell: ({ value }) => ViewTableUtils.renderPercentage({ value: value * 100 })
		},
		{
			type: 'actions',
			field: 'actions',
			width: 40,
			renderCell: ({ id, row }) =>
				ViewTableUtils.renderMenu({
					items: [
						{
							name: 'VIEW_ITEM_BUTTON_EDIT',
							icon: 'edit',
							hidden: ({ authorizer }) => authorizer.cannot('update', 'sys_kpis_goals'),
							action: ({ dispatch, model }) => {
								dispatch(showKpisGoalsEditDialog({ model, aggregation, updateRefresh }));
							}
						},
						{
							name: 'VIEW_ITEM_BUTTON_DELETE',
							icon: 'delete',
							hidden: ({ authorizer }) => authorizer.cannot('delete', 'sys_kpis_goals'),
							action: ({ dispatch, model }) => {
								dispatch(
									showDeleteDialog({
										module: 'sys_kpis_goals',
										id: model.kpgl_uid,
										hardDelete: true,
										updateRefresh
									})
								);
							}
						}
					],
					buttonProps: { settings, model: row, updateRefresh }
				}),
			sortable: false
		}
	);

	return columns;
};

export default getKpisGoalsValuesTableColumns;
